import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux-store/hooks";
import { useSnackbar } from "notistack";

import { RenderCellExpand } from "shared-components/expanded-cel";
import TableData from "shared-components/table-data";
import PaginationFooter from "shared-components/pagination";
import { loadingHelper } from "constants/loadingHelper";
import { limit } from "constants/pagination-constants";
import {
  ADD_DEFAULT_WARRANTY,
  ADD_WARRANTY,
  EDIT_WARRANTY,
} from "constants/route/route-constants";

import {
  GridCellParams,
  GridColDef,
  GridRowModel,
} from "@material-ui/data-grid";

import DeleteUser from "pages/user-profile/components/delete-user";
import { DeletePopupModal } from "pages/best-deals/components/deals-table/styles";

import {
  getAllWarrantiesThunk,
  getDefaultWarrantiesThunk,
  deleteBrandModelWarrantyThunk,
} from "slices/warranty";
import { getApiErrorMessage } from "utils/api-error-utils";
import {
  UnifiedTableContainer,
  SectionTitleBar,
  SectionTitleText,
  SectionTitleAction,
  SectionTitleAddIcon,
  WarrantyHeaderGrid,
  WarrantyDefaultGrid,
  WarrantyModelGrid,
} from "pages/management/warranty/styles";
import {
  DateDiv,
  PdfLink,
  ActionIconsCell,
} from "pages/management/warranty/warranty-table/styles";
import PdfPreviewModal from "pages/management/warranty/components/pdf-preview-modal";
import {
  EditIconLogo,
  RemoveIconLogo,
} from "pages/user-profile/components/user-management/styles";

const handelDateFormat = (timedate: string | undefined) => {
  if (!timedate) {
    return "N/A";
  }
  const day = new Date(timedate);
  if (Number.isNaN(day.getTime())) {
    return String(timedate);
  }
  const time = day.toLocaleString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
  return `${day.getDate()}-${day.getMonth() + 1}-${day.getFullYear()} ${time}`;
};

const hasPdfUrl = (url: unknown): url is string =>
  url != null && String(url).trim() !== "";

const WarrantyTable = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [defaultLoading, setDefaultLoading] = useState(false);
  const [modelLoading, setModelLoading] = useState(false);
  const [defaultRows, setDefaultRows] = useState<GridRowModel[]>([]);
  const [modelRows, setModelRows] = useState<GridRowModel[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandOpen, setExpandOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedDeleteID, setSelectedDeleteID] = useState<number>(0);
  const [modalMessage, setModalMessage] = useState<{
    headerText: string;
    message: React.ReactNode;
    paraText: string;
  }>({
    headerText: "",
    message: "",
    paraText: "",
  });

  const warranties = useAppSelector((state) => state.warranty.warrantyInfo);
  const brandName = useAppSelector((state) => state.brand.brandType);

  const loading = defaultLoading || modelLoading;
  const paginationSize = Math.ceil(modelRows.length / limit);
  const paginatedModelRows = useMemo(() => {
    const start = (currentPage - 1) * limit;
    return modelRows.slice(start, start + limit);
  }, [modelRows, currentPage]);

  useEffect(() => {
    loadingHelper(
      dispatch(getDefaultWarrantiesThunk()),
      setDefaultLoading,
      enqueueSnackbar
    );
    loadingHelper(
      dispatch(getAllWarrantiesThunk()),
      setModelLoading,
      enqueueSnackbar
    );
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, brandName]);

  useEffect(() => {
    setDefaultRows(warranties?.defaultWarranties ?? []);
    setModelRows(warranties?.allWarranties ?? []);
  }, [warranties]);

  useEffect(() => {
    if (paginationSize > 0 && currentPage > paginationSize) {
      setCurrentPage(paginationSize);
    }
    if (paginationSize === 0) {
      setCurrentPage(1);
    }
  }, [paginationSize, currentPage]);

  const handleViewPdf = (
    e: React.MouseEvent<HTMLButtonElement>,
    url: string
  ) => {
    e.stopPropagation();
    setSelectedPdfUrl(url);
    setExpandOpen(true);
  };

  const handleClosePdfPreview = () => {
    setExpandOpen(false);
    setSelectedPdfUrl("");
  };

  const renderPdfCell = (url: unknown) => {
    if (!hasPdfUrl(url)) {
      return <>N/A</>;
    }
    return (
      <PdfLink type="button" onClick={(e) => handleViewPdf(e, url)}>
        View PDF
      </PdfLink>
    );
  };

  const handleEdit = (
    e: React.MouseEvent,
    rowData: GridRowModel,
    isDefault: boolean
  ) => {
    e.stopPropagation();
    history.push({
      pathname: EDIT_WARRANTY,
      state: {
        detail: "edit",
        rowData,
        isDefault,
      },
    });
  };

  const handleDelete = (e: React.MouseEvent, rowData: GridRowModel) => {
    e.stopPropagation();
    setSelectedDeleteID(rowData.id);
    setModalMessage({
      headerText: "Delete Warranty",
      message: (
        <>
          Are you sure you want to delete warranty for{" "}
          <span style={{ color: "red" }}>{rowData.carModel}</span>?
        </>
      ),
      paraText: "All warranty PDF data for this entry will be deleted.",
    });
    setOpenDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setOpenDeleteModal(false);
    setSelectedDeleteID(0);
  };

  const confirmDelete = async (id: number) => {
    try {
      setDeleteLoading(true);
      const result = await dispatch(deleteBrandModelWarrantyThunk(id)).unwrap();
      const successMessage =
        typeof result?.response?.data === "string"
          ? result.response.data
          : "Warranty deleted successfully";
      enqueueSnackbar(successMessage, { variant: "success" });
      closeDeleteModal();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, "Failed to delete warranty"), {
        variant: "error",
      });
      closeDeleteModal();
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePagination = (_event: unknown, pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const buildColumns = (
    isDefaultSection: boolean,
    pageOffset = 0
  ): GridColDef[] => [
    {
      field: "serialNo",
      headerName: "S.no.",
      sortable: false,
      flex: 0.12,
      renderCell: (params: GridCellParams) => {
        return pageOffset + params.api.getRowIndex(params.id) + 1;
      },
    },
    {
      field: "createdOn",
      headerName: "Created on",
      sortable: false,
      flex: 0.28,
      renderCell: (params: GridCellParams) => {
        return (
          <DateDiv>{handelDateFormat(params.row?.createdOn)}</DateDiv>
        );
      },
    },
    {
      field: "carModel",
      headerName: "Car model",
      sortable: false,
      resizable: false,
      flex: 0.3,
      renderCell: RenderCellExpand,
    },
    {
      field: "standardWarrantyPdf",
      headerName: "Standard warranty PDF",
      sortable: false,
      resizable: false,
      flex: 0.35,
      renderCell: (params: GridCellParams) => {
        return renderPdfCell(params.row?.standardWarrantyPdf);
      },
    },
    {
      field: "extendedWarrantyPdf",
      headerName: "Extended warranty PDF",
      sortable: false,
      resizable: false,
      flex: 0.35,
      renderCell: (params: GridCellParams) => {
        return renderPdfCell(params.row?.extendedWarrantyPdf);
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      resizable: false,
      flex: 0.22,
      renderCell: (params: GridCellParams) => {
        const rowData = params.row;
        return (
          <ActionIconsCell>
            <EditIconLogo
              onClick={(e) => handleEdit(e, rowData, isDefaultSection)}
            />
            <RemoveIconLogo onClick={(e) => handleDelete(e, rowData)} />
          </ActionIconsCell>
        );
      },
    },
  ];

  const defaultColumns = useMemo(() => buildColumns(true), []);
  const modelColumns = useMemo(
    () => buildColumns(false, (currentPage - 1) * limit),
    [currentPage]
  );

  const showDefaultAddIcon = !loading && defaultRows.length === 0;

  return (
    <>
      <UnifiedTableContainer>
        <WarrantyHeaderGrid>
          <TableData
            rows={[]}
            isLoading={false}
            columns={modelColumns}
            isCheckBox={false}
            pageSize={1}
            isPagination={true}
            embedded
          />
        </WarrantyHeaderGrid>

        <SectionTitleBar>
          <SectionTitleText>Default Warranty PDFs</SectionTitleText>
          {showDefaultAddIcon ? (
            <SectionTitleAction
              type="button"
              onClick={() => history.push(ADD_DEFAULT_WARRANTY)}
            >
              Add Default PDFs
              <SectionTitleAddIcon />
            </SectionTitleAction>
          ) : null}
        </SectionTitleBar>
        <WarrantyDefaultGrid>
          <TableData
            rows={loading ? [] : defaultRows}
            isLoading={loading}
            columns={defaultColumns}
            isCheckBox={false}
            pageSize={1}
            isPagination={true}
            hideHeader
            embedded
            rowHeight={65}
            getRowId={(row) => `default-${row.id}`}
          />
        </WarrantyDefaultGrid>

        <SectionTitleBar>
          <SectionTitleText>Modelwise Warranty PDFs</SectionTitleText>
          <SectionTitleAction
            type="button"
            onClick={() => history.push(ADD_WARRANTY)}
          >
            Add Modelwise PDFs
            <SectionTitleAddIcon />
          </SectionTitleAction>
        </SectionTitleBar>
        <WarrantyModelGrid>
          <TableData
            rows={loading ? [] : paginatedModelRows}
            isLoading={loading}
            columns={modelColumns}
            isCheckBox={false}
            pageSize={limit}
            isPagination={true}
            hideHeader
            embedded
            rowHeight={65}
            getRowId={(row) => `model-${row.id}`}
          />
        </WarrantyModelGrid>
      </UnifiedTableContainer>

      {paginationSize > 1 ? (
        <PaginationFooter
          handleChange={(event, value) => {
            handlePagination(event, value);
          }}
          dataSize={paginationSize}
          pageNumber={currentPage}
        />
      ) : null}

      <PdfPreviewModal
        open={expandOpen}
        onClose={handleClosePdfPreview}
        pdfUrl={selectedPdfUrl}
        title="Warranty PDF expanded view"
      />

      <DeletePopupModal open={openDeleteModal} onClose={closeDeleteModal}>
        <DeleteUser
          params={selectedDeleteID}
          closeModal={closeDeleteModal}
          confirmDelete={confirmDelete}
          loading={deleteLoading}
          headerText={modalMessage.headerText}
          paraText={modalMessage.paraText}
          message={modalMessage.message as string}
        />
      </DeletePopupModal>
    </>
  );
};

export default WarrantyTable;
